import React from "react";

/**
 * Skeleton component for loading states
 * @param {Object} props - Component props
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.variant - Skeleton variant (text, circular, rectangular)
 * @param {number} props.width - Width of the skeleton
 * @param {number} props.height - Height of the skeleton
 * @param {boolean} props.animate - Whether to show animation (default: true)
 */
export const Skeleton = ({
  className = "",
  variant = "rectangular",
  width,
  height,
  animate = true,
}) => {
  const baseClasses = "bg-muted rounded";
  const variantClasses = {
    text: "h-4",
    circular: "rounded-full",
    rectangular: "h-4",
  };

  const animationClass = animate ? "animate-pulse" : "";
  const widthClass = width ? `w-${width}` : "";
  const heightClass = height ? `h-${height}` : "";

  const classes = [
    baseClasses,
    variantClasses[variant],
    animationClass,
    widthClass,
    heightClass,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return <div className={classes} style={{ width, height }} />;
};

/**
 * Skeleton for metric cards in OverviewPage
 */
export const MetricCardSkeleton = ({ className = "" }) => (
  <div
    className={`border-border bg-background animate-pulse rounded-lg border p-4 ${className}`}
  >
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <Skeleton className="mb-2" />
        <Skeleton className="mb-2 h-8" />
        <Skeleton className="w-16" />
      </div>
      <Skeleton className="h-8 w-8" />
    </div>
  </div>
);

/**
 * Skeleton for popular items/categories in OverviewPage
 */
export const PopularItemsSkeleton = ({
  titleWidth = "w-48",
  itemCount = 5,
  className = "",
}) => (
  <div
    className={`border-border bg-background animate-pulse rounded-lg border p-6 ${className}`}
  >
    <Skeleton className={`mb-4 h-6 ${titleWidth}`} />
    <div className="space-y-3">
      {Array.from({ length: itemCount }).map((_, index) => (
        <div key={index} className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Skeleton className="h-8 w-8" />
            <div className="space-y-1">
              <Skeleton className="w-32" />
              <Skeleton className="w-20" />
            </div>
          </div>
          <Skeleton className="w-16" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Skeleton for order rows in OverviewPage
 */
export const OrderRowSkeleton = ({ className = "" }) => (
  <div
    className={`border-border flex animate-pulse items-center justify-between border-b p-4 last:border-b-0 ${className}`}
  >
    <div className="flex items-center space-x-4">
      <Skeleton className="h-5 w-5" />
      <div className="space-y-1">
        <Skeleton className="w-20" />
        <Skeleton className="w-32" />
      </div>
    </div>
    <div className="flex items-center space-x-4">
      <div className="space-y-1 text-right">
        <Skeleton className="w-16" />
        <Skeleton className="w-12" />
      </div>
      <Skeleton className="h-6 w-16" />
    </div>
  </div>
);

/**
 * Skeleton for customer satisfaction in OverviewPage
 */
export const CustomerSatisfactionSkeleton = ({ className = "" }) => (
  <div
    className={`border-border bg-background animate-pulse rounded-lg border p-6 ${className}`}
  >
    <Skeleton className="mb-6 h-6 w-40" />
    <div className="bg-card/50 rounded-lg p-4">
      <div className="text-center">
        <Skeleton className="mx-auto mb-4 h-24 w-24 rounded-full" />
        <Skeleton className="mx-auto w-32" />
      </div>
    </div>
  </div>
);

/**
 * Skeleton for filter section in OverviewPage
 */
export const FilterSkeleton = ({ className = "" }) => (
  <div
    className={`border-border bg-background mb-6 animate-pulse rounded-lg border p-4 ${className}`}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-4">
        <Skeleton className="w-24" />
        <Skeleton className="h-10 w-40" />
      </div>
    </div>
  </div>
);
